import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import { Volume as VolumeSchema } from '@schema';
import { computeNotificationsHandler, OperationActions } from '@/common/utils/computeNotificationsHandler';
import mapper, { Volume } from '@/common/store/pinia/computeVolumes/computeVolumeMapper';
import useAuthStore from '@/common/store/pinia/auth';
import { PROMETHEUS_API_URL } from '@/common/constants/prometheus.constants';
import api from '@/common/utils/api';
import { ref } from 'vue';

const ResourceUrl = '/api/v2/compute/volumes';

export const useComputeVolumesStore = defineStore('computeVolumes', () => {
  const commonStore = useCommonStore<VolumeSchema & { error?: string }, Volume>({
    getUrl: () => ResourceUrl,
    mapItem: mapper,
    pagination: 'common',
    dataChangeMode: 'event'
  })();
  const authStore = useAuthStore();

  const { removeItem, changeItem, fetchItemsWithFiltersThrottled } = commonStore;

  const volumesUsage = ref<Record<ID, number>>({});

  const fetchUsage = async ({ id: volumeId, fileId }: Pick<Volume, 'id' | 'fileId'>, params?: object) => {
    try {
      const response = await api.get(`${PROMETHEUS_API_URL}/query`,
        {
          query: `libvirt_domain_block_stats_disk_physical{source_file=~".*volume-${fileId}/volume-${fileId}.*"}`,
          ...params
        },
        { headers: { [api.scopedTokenHeaderName]: authStore.currentUser?.scoped_token } });

      if (response.data && response.data.status === 'success') {
        if (response.data.data.result.length === 1) {
          volumesUsage.value[volumeId] = response.data.data.result[0].value[1];
        } else {
          volumesUsage.value[volumeId] = 0;
        }
      } else {
        volumesUsage.value[volumeId] = 0;
      }
    } catch (e) {
      volumesUsage.value[volumeId] = 0;
      throw e;
    }
  };

  const relocate = (id: ID, host: string) => api.post(`${ResourceUrl}/${id}/relocate/`, { host });

  const onCompute = async (event: SocketEvent<Volume>): Promise<void> => {
    if (!event) {
      return;
    }

    const onWsUpdate = (obj: Partial<Volume>) => {
      changeItem(obj, false);
      return fetchItemsWithFiltersThrottled(obj.id as ID);
    };

    const operationActions: OperationActions<Volume> = {
      created: { fn: onWsUpdate },
      updated: { fn: onWsUpdate },
      failed: { fn: onWsUpdate },
      deleted: { fn: (obj) => {
        fetchItemsWithFiltersThrottled.clearId(obj.id);
        removeItem(obj.id);
      } },
    };

    await computeNotificationsHandler<Volume>({
      eventType: 'volumes',
      resource: 'compute_volumes',
      operationActions
    })(event);
  };

  return {
    ...commonStore,
    volumesUsage,
    onCompute,
    fetchUsage,
    relocate
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useComputeVolumesStore;
